import { useRef, useState } from "react";
import { FileText, Loader2, X, UploadCloud } from "lucide-react";
import { http, apiError } from "../../lib/api";

const EXTS = ["pdf", "doc", "docx"];
const MAX = 10 * 1024 * 1024;

export const SingleFileUpload = ({ label, required, value, onChange, testid }) => {
  const input = useRef(null);
  const [state, setState] = useState({ busy: false, progress: 0, error: "" });
  const pick = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!EXTS.includes(ext)) return setState({ busy: false, progress: 0, error: "Please upload a PDF, DOC or DOCX file." });
    if (file.size > MAX) return setState({ busy: false, progress: 0, error: "Files must be 10 MB or smaller." });
    setState({ busy: true, progress: 0, error: "" });
    onChange(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await http.post("/careers/upload", fd, { onUploadProgress: (e) => setState((s) => ({ ...s, progress: e.total ? Math.round((e.loaded / e.total) * 100) : 50 })) });
      onChange({ id: data?.id, token: data?.token, name: data?.name || file.name, size: file.size });
      setState({ busy: false, progress: 100, error: "" });
    } catch (e) {
      setState({ busy: false, progress: 0, error: apiError(e) });
    }
  };
  return (
    <div data-testid={testid}>
      <p className="label">{label}{required ? " *" : <span className="text-slate-400 normal-case tracking-normal"> (optional)</span>}</p>
      <input ref={input} type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden"
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid={`${testid}-input`} />
      {value ? (
        <div className="flex items-center gap-3 border hairline px-4 py-3" data-testid={`${testid}-done`}>
          <FileText className="w-5 h-5 text-gold shrink-0" />
          <span className="text-sm text-slate-100 truncate flex-1">{value.name}</span>
          <span className="text-xs text-slate-400">{(value.size / 1024).toFixed(0)} KB</span>
          <button type="button" onClick={() => onChange(null)} className="text-slate-400 hover:text-rose-300" aria-label="Remove file" data-testid={`${testid}-remove`}><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <button type="button" onClick={() => input.current?.click()} disabled={state.busy} className="w-full border border-dashed border-white/20 hover:border-[var(--gold)] px-4 py-6 flex flex-col items-center gap-2 text-sm text-slate-300 transition-colors" data-testid={`${testid}-button`}>
          {state.busy ? <><Loader2 className="w-5 h-5 animate-spin text-gold" /> Uploading… {state.progress}%</> : <><UploadCloud className="w-5 h-5 text-gold" /> Choose a file <span className="text-xs text-slate-400">PDF, DOC or DOCX, up to 10 MB</span></>}
        </button>
      )}
      {state.error && <p className="text-xs text-rose-300 mt-2" data-testid={`${testid}-error`}>{state.error}</p>}
    </div>
  );
};
